import { portalText, usePortalTranslation, portalLocale } from "../../../i18n/portal.js";
/**
 * `data-screen-label="Agent"`, tab `analytics` — P11-01.
 *
 * EVERY NUMBER ON THIS TAB IS A MEASUREMENT, AND EVERY PERCENTAGE IS DIVIDED HERE.
 * `GET /v1/agents/{id}/analytics` ships **counts** and one denominator; nothing on the wire
 * is a share. That is not fussiness: two shares rounded server-side do not add to 100, and a
 * share of a window the reader cannot see is not checkable. `shareOf` returns `null` for a
 * zero denominator, and the screen prints "—" rather than a bar of length zero, because a
 * share of nothing is nothing rather than 0%.
 *
 * THE CHART IS DRAWN FROM THE SERIES, NOT DECORATED WITH ONE. The prototype's polyline is a
 * fixed set of coordinates; this one is computed from `daily`, so the shape of the line is
 * the shape of the data and an empty window draws no line at all. The days are cut in the
 * **workspace's** time zone, which the response names; the design prints no zone, so the label's
 * `title` carries it — a day boundary that is not the customer's is somebody else's week.
 *
 * WHAT THE DESIGN ASKS FOR THAT IS NOT DRAWN, AND WHY — the P10 discipline, applied here:
 *
 *   · **No sparkline on the four figures.** The API has one series, per day, for the whole
 *     window; it does not carry a per-figure series, so a sparkline under "Average length"
 *     would be a drawing rather than a measurement.
 *   · **No comparison against the previous period.** The design shows none, and the route
 *     reads one window. Inventing "+12% on last week" would need a second window nothing
 *     asks for and nothing has agreed the length of.
 *   · **"Answer them in Review" (was "Add these to the documents") opens Review rather than the wizard.**
 *     Turning a ranked question into a queued answer is exactly what Review's Gaps pane
 *     does, and it is the only place where the answer is recorded against the gap.
 *
 * AMENDED 2026-10-19 (plan-021 WAPP-HIST, design §33.13.4, ED-33, OD-7): the "Passes" block — how many conversations
 * this VoiceAgent passed to other agents of the workspace (by agent, with a summary or straight away), how many were
 * passed to it, the passes back it made and received, the passes that could not happen (by reason), the share of the
 * conversations it answered first that it passed on, and how long it answered. Counts, never money: talk time and money
 * stay with the VoiceAgent that answered first, so the figures above are unchanged. "No conversation was passed in this
 * period." when there is nothing to count.
 *
 * AMENDED 2026-10-28 (plan-029 WAPP-HIST1, design §40.19.6, FR-CP-13, AC-10): "Refused calls" and "Ended by caller
 * protection", by reason — the refusals were never answered or billed and are in no figure above; the ends were answered,
 * billed to the end and are counted above. Counts only.
 */
import { useQuery } from "@tanstack/react-query";
import {
  Icon,
  Segmented,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@wetalk/ui";
import type { CSSProperties, ReactNode } from "react";

import { GLYPH } from "../../../shell/glyph.js";
import { useGateway } from "../../../api/provider.js";
import { languageNameOf, priceUnavailableNoteOf } from "../../../api/conversation-view.js";
import { voiceAgentAnalyticsQuery } from "../../../api/query.js";
import { shareOf } from "../../../api/voice-agent-view.js";
import { meterStyle } from "../../../meter.js";
import { formatDuration, formatMoney } from "../../../money.js";
import { talkTimeText } from "../../../talk-time.js";
import type {
  AnalyticsRange,
  AnalyticsSliceView,
  VoiceAgentAnalyticsView,
} from "../../../api/gateway.js";
import {
  analyticsVoiceAgentPassIsEmpty,
  refusedByReasonTextOf,
  voiceAgentPassModeTextOf,
  type AnalyticsVoiceAgentPassView,
} from "../../../api/voice-agent-stint-view.js";
import {
  partyProtectionReasonShortText,
  partyReasonTotalOf,
  type PartyEndedByReasonView,
  type PartyProtectionEndReason,
  type PartyRefusedByReasonView,
} from "../../../api/party-protection-view.js";
import type { AnalyticsOutboundView } from "../../../api/outbound-view.js";

/** The design's five windows (`anaRanges`). "all" stays a valid URL value; it is not offered. */
const RANGE_OPTION = [
  { value: "24h", get label() { return portalText("operations", "24_hours_cb4780f8"); } },
  { value: "7d", get label() { return portalText("operations", "7_days_0a8dd6ad"); } },
  { value: "14d", get label() { return portalText("operations", "14_days_176e595a"); } },
  { value: "30d", get label() { return portalText("operations", "30_days_7d4278a8"); } },
  { value: "90d", get label() { return portalText("operations", "90_days_170621ac"); } },
] as const;

/** What the figures count, which the design does not print: the figures' `title`. */
const COUNTED =
  () => (portalText("operations", "the_counts_are_answered_conversations_only_and_exclude_tes_5a6e5295"));

/**
 * Review 120: what History lists and these figures leave out, in one sentence, or `null` when
 * nothing was left out. History showed a test call from today while Analytics said "No
 * conversations in this window"; both were right, and neither said why.
 */
export function excludedNoteOf(testCallCount: number, unansweredCount: number): string | null {
  const part: string[] = [];
  if (testCallCount > 0) part.push(portalText("operations", "value_test_value_31f5993e", { v0: String(String(testCallCount)), v1: String(testCallCount === 1 ? portalText("operations", "call_bc8d8647") : portalText("operations", "calls_51b6cb25")) }));
  if (unansweredCount > 0) {
    part.push(portalText("operations", "value_unanswered_value_d943a2ab", { v0: String(String(unansweredCount)), v1: String(unansweredCount === 1 ? portalText("operations", "call_bc8d8647") : portalText("operations", "calls_51b6cb25")) }));
  }
  if (part.length === 0) return null;
  const listed = part.join(portalText("operations", "opening.period.and"));
  const total = testCallCount + unansweredCount;
  return portalText("operations", "not_counted_here_value_in_this_window_history_lists_value_c5a23f7c", { v0: String(listed), v1: String(total === 1 ? portalText("operations", "it_6c5522ca") : portalText("operations", "them_98910a6e")) });
}

/**
 * Whole percentages that add up to exactly 100 — the largest-remainder method (review #114:
 * "88% + 13%"). Each share is floored, and the points left over go to the largest remainders,
 * ties to the earlier row. `null` for every row when there is nothing to divide by.
 */
export function sharesOf(counts: readonly number[], total: number): readonly (number | null)[] {
  if (total <= 0) return counts.map(() => null);
  const exact = counts.map((count) => (count * 100) / total);
  const floored = exact.map((one) => Math.floor(one));
  const whole = Math.round((counts.reduce((sum, count) => sum + count, 0) * 100) / total);
  let left = whole - floored.reduce((sum, one) => sum + one, 0);
  const ranked = exact
    .map((one, index) => ({ index, remainder: one - Math.floor(one) }))
    .sort((a, b) => b.remainder - a.remainder || a.index - b.index);
  for (const { index } of ranked) {
    if (left <= 0) break;
    floored[index] = (floored[index] ?? 0) + 1;
    left -= 1;
  }
  return floored;
}

/** A language slice's label in words: `el` → "Greek"; "Never settled" stays as it is. */
function languageLabelOf(label: string): string {
  return /^[a-z]{2,3}(-[A-Za-z0-9]{2,8})*$/.test(label) ? languageNameOf(label) : label;
}

/** The chart's own coordinate space. Unitless, so the SVG scales with its container. */
const CHART = { width: 600, height: 170, floor: 150, ceiling: 22 } as const;

export interface AnalyticsTabProps {
  readonly voiceAgentId: string;
  readonly range: AnalyticsRange;
  readonly onRange: (value: AnalyticsRange) => void;
  /** Opens Review's Gaps pane, which is where a ranked question becomes a queued answer. */
  readonly onOpenGaps: () => void;
}

export function AnalyticsTab(props: AnalyticsTabProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const analytics = useQuery(voiceAgentAnalyticsQuery(gateway, props.voiceAgentId, props.range));

  if (analytics.data === undefined) {
    return <p className="c-note">{portalText("operations", "adding_the_window_up_698ff700")}</p>;
  }

  const one = analytics.data;
  const successPercent = shareOf(one.outcomeReachedCount, one.conversationCount);

  const rangeText =
    RANGE_OPTION.find((option) => option.value === props.range)?.label ?? props.range;
  const mediumShare = sharesOf(
    one.byMedium.map((row) => row.conversationCount),
    one.conversationCount,
  );
  const zoneTitle = portalText("operations", "days_and_hours_are_cut_in_value_the_workspace_s_time_zone_b38b5e01", { v0: String(one.timeZone) });
  const excludedNote = excludedNoteOf(one.excludedTestCallCount, one.excludedUnansweredCount);

  return (
    <>
      <div className="c-va-analytics-head">
        <p className="c-va-label c-va-label-flush" title={zoneTitle}>
          {portalText("operations", "calls_a_day_b105e005")}{props.range === "all" ? portalText("operations", "all_time_5ae5c422") : portalText("operations", "last_value_f85dacfe", { v0: String(rangeText) })}
        </p>
        <Segmented
          name="analyticsRange"
          legend={portalText("operations", "range_a16c5bbe")}
          value={props.range}
          options={RANGE_OPTION}
          onChange={props.onRange}
        />
      </div>

      <DailyChart daily={one.daily} />
      {excludedNote === null ? null : <p className="c-note">{excludedNote}</p>}
      {one.priceUnavailableCount === 0 ? null : <p className="c-note">{priceUnavailableNoteOf(one.priceUnavailableCount)}</p>}

      <div className="c-va-figures c-va-analytics-figures" title={COUNTED()}>
        <div>
          <span className="c-va-figure-value">{one.conversationCount.toLocaleString((portalLocale() === "el" ? "el-GR" : "en-GB"))}</span>
          <span className="c-va-figure-label">{portalText("operations", "calls_0a19b7e2")}</span>
        </div>
        <div>
          <span className="c-va-figure-value">
            {one.averageSeconds === null ? "—" : formatDuration(one.averageSeconds)}
          </span>
          <span className="c-va-figure-label">{portalText("operations", "average_length_2df99da9")}</span>
        </div>
        <div
          title={
            one.trialCovered
              ? portalText("operations", "value_at_list_price_your_free_minutes_are_used_first_33743d13", { v0: String(formatMoney(one.listSpend)) })
              : undefined
          }
        >
          <span className="c-va-figure-value">{talkTimeText(one.talkSecond)}</span>
          <span className="c-va-figure-label">
            {portalText("operations", "minutes_8ba3b033")}{formatMoney(one.spend)}
            {one.trialCovered ? portalText("operations", "value_at_list_price_free_minutes_used_2eec5e82", { v0: String(formatMoney(one.listSpend)) }) : ""}
          </span>
        </div>
        <div>
          <span className="c-va-figure-value">
            {successPercent === null ? "—" : `${String(successPercent)}%`}
          </span>
          <span className="c-va-figure-label">{one.successMetricLabel}</span>
        </div>
      </div>

      <div className="c-va-split">
        <div>
          <p className="c-va-label">{portalText("operations", "outcomes_6848ce55")}</p>
          <CountTable caption={portalText("operations", "outcomes_6848ce55")} slice={one.outcome} total={one.conversationCount} />
        </div>
        <div>
          <p className="c-va-label">{portalText("operations", "languages_db07be18")}</p>
          <ShareList
            slice={one.language.map((row) => ({ ...row, label: languageLabelOf(row.label) }))}
            total={one.conversationCount}
          />
        </div>
      </div>

      <div className="c-va-split c-va-analytics-lower">
        <div>
          <p className="c-va-label">{portalText("operations", "by_channel_2efebe9a")}</p>
          <div className="c-va-plain-table">
            <Table caption={portalText("operations", "conversations_by_medium_and_direction_eccc78dd")}>
              <TableBody>
                {one.byMedium.map((row, index) => {
                  const share = mediumShare[index] ?? null;
                  return (
                    <TableRow key={`${row.medium}:${row.direction}:${row.channelLabel}`}>
                      <TableCell>
                        <span className="c-va-medium">
                          <Icon
                            glyph={
                              row.channelLabel === "web_chat"
                                ? GLYPH.chatText
                                : row.channelLabel === "website_voice"
                                  ? GLYPH.browser
                                  : row.direction === "outbound"
                                    ? GLYPH.phoneOutgoing
                                    : GLYPH.phoneCall
                            }
                            size="lg"
                          />
                          {row.label}
                        </span>
                      </TableCell>
                      <TableCell numeric>{row.conversationCount.toLocaleString((portalLocale() === "el" ? "el-GR" : "en-GB"))}</TableCell>
                      <TableCell numeric>
                        <span className="c-va-muted">{share === null ? "—" : `${String(share)}%`}</span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <p className="c-va-label c-va-label-gap" title={zoneTitle}>
            {portalText("operations", "busiest_hours_995bc2d7")}</p>
          <BusiestHours hour={one.hour} />
        </div>

        <div>
          <p className="c-va-label">{portalText("operations", "since_the_last_edit_b06b8034")}</p>
          <div className="c-va-plain-table c-va-table-wrap">
            <Table caption={portalText("operations", "conversations_and_outcomes_per_agent_version_b741e19f")}>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{portalText("operations", "version_2da600bf")}</TableHeaderCell>
                  <TableHeaderCell numeric>{portalText("operations", "calls_0a19b7e2")}</TableHeaderCell>
                  <TableHeaderCell numeric>{portalText("operations", "success_42a8f651")}</TableHeaderCell>
                  <TableHeaderCell numeric>{portalText("operations", "avg_cdc93143")}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {one.agentVersion.map((row) => {
                  const share = shareOf(row.outcomeReachedCount, row.conversationCount);
                  return (
                    <TableRow key={row.agentVersionId}>
                      <TableCell>
                        <span className={row.isLive ? "c-va-version-cell c-va-version-cell-live" : "c-va-version-cell"}>
                          {row.label}
                          {row.isLive ? portalText("operations", "live_cdc53192") : ""}
                        </span>
                      </TableCell>
                      <TableCell numeric>{row.conversationCount.toLocaleString((portalLocale() === "el" ? "el-GR" : "en-GB"))}</TableCell>
                      <TableCell numeric>{share === null ? "—" : `${String(share)}%`}</TableCell>
                      <TableCell numeric>
                        {row.averageSeconds === null ? "—" : formatDuration(row.averageSeconds)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <p className="c-va-label c-va-label-gap">{portalText("operations", "questions_the_agent_could_not_answer_1b52da5a")}</p>
          {one.gap.length === 0 ? (
            <p className="c-note">
              {portalText("operations", "nothing_unanswered_is_waiting_every_question_callers_asked_9e68e471")}</p>
          ) : (
            <>
              <div className="c-va-ranked">
                {one.gap.map((row) => (
                  <span key={row.gapId} className="c-va-ranked-row">
                    <span>{row.question}</span>
                    <span className="c-va-muted c-va-tabular">
                      {String(row.callerCount)} {row.callerCount === 1 ? portalText("operations", "caller_14dc69de") : portalText("operations", "callers_5bbff698")}
                    </span>
                  </span>
                ))}
              </div>
              {/* Design sync 2026-10-09: a link, "Answer them in Review", where the button was. */}
              <a
                href="#"
                className="c-va-ranked-link"
                onClick={(event) => {
                  event.preventDefault();
                  props.onOpenGaps();
                }}
              >
                {portalText("operations", "answer_them_in_review_fb23b384")}</a>
            </>
          )}
        </div>
      </div>

      {one.voiceAgentPass === undefined ? null : <VoiceAgentPassBlock block={one.voiceAgentPass} />}
      {one.refusedByReason === undefined || one.endedByReason === undefined ? null : (
        <PartyProtectionBlock refusedByReason={one.refusedByReason} endedByReason={one.endedByReason} />
      )}
      {one.outbound === undefined ? null : <OutboundVoicemailBlock block={one.outbound} />}
    </>
  );
}

/**
 * plan-031 VMD-WAPP (design §42.11; FR-VMD-14): the window's answered outbound conversations and how many of them a
 * voicemail answered — "Voicemail, hung up". Drawn only once a voicemail has been hung up on: a nought beside a detection
 * that is off or only listening would read as a claim that it runs. Counts, never a number or a price.
 */
export function OutboundVoicemailBlock({ block }: { readonly block: AnalyticsOutboundView }): ReactNode {
  usePortalTranslation("operations");
  if (block.voicemailCount === 0) return null;
  const figure: readonly { readonly label: string; readonly value: string }[] = [
    { label: portalText("operations", "voicemail.analytics_answered"), value: countText(block.answeredOutboundCount) },
    { label: portalText("operations", "voicemail.analytics_voicemail"), value: countText(block.voicemailCount) },
  ];
  return (
    <>
      <p className="c-va-label c-va-label-gap">{portalText("operations", "voicemail.analytics_title")}</p>
      <div className="c-va-figures c-va-analytics-figures">
        {figure.map((one) => (
          <div key={one.label}>
            <span className="c-va-figure-value">{one.value}</span>
            <span className="c-va-figure-label">{one.label}</span>
          </div>
        ))}
      </div>
      <p className="c-note">{portalText("operations", "voicemail.analytics_note")}</p>
    </>
  );
}

/** One by-reason table: the reasons in their short words, each with its count. */
function PartyReasonTable({
  caption,
  counts,
}: {
  readonly caption: string;
  readonly counts: Readonly<Partial<Record<PartyProtectionEndReason, number>>>;
}): ReactNode {
  const row = (Object.entries(counts) as [PartyProtectionEndReason, number][]).map(([reason, count]) => ({ reason, count }));
  return (
    <div className="c-va-plain-table">
      <Table caption={caption}>
        <TableHead>
          <TableRow>
            <TableHeaderCell>{portalText("callerProtection", "analytics.reason")}</TableHeaderCell>
            <TableHeaderCell numeric>{portalText("callerProtection", "analytics.count")}</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {row.map((one) => (
            <TableRow key={one.reason}>
              <TableCell>{partyProtectionReasonShortText(one.reason)}</TableCell>
              <TableCell numeric>{countText(one.count)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/**
 * plan-029 WAPP-HIST1 (design §40.19.6; FR-CP-13; AC-10): the window's calls caller protection refused (never answered,
 * never billed, in no figure above) and the answered conversations it ended (billed, counted above), by reason. Counts,
 * never a number. One sentence when there is nothing to count.
 */
export function PartyProtectionBlock({
  refusedByReason,
  endedByReason,
}: {
  readonly refusedByReason: PartyRefusedByReasonView;
  readonly endedByReason: PartyEndedByReasonView;
}): ReactNode {
  usePortalTranslation("callerProtection");
  const refusedTotal = partyReasonTotalOf(refusedByReason);
  const endedTotal = partyReasonTotalOf(endedByReason);
  if (refusedTotal === 0 && endedTotal === 0) {
    return (
      <>
        <p className="c-va-label c-va-label-gap">{portalText("callerProtection", "analytics.refused_title")}</p>
        <p className="c-note">{portalText("callerProtection", "analytics.empty")}</p>
      </>
    );
  }
  return (
    <div className="c-va-split c-va-analytics-lower">
      <div>
        <p className="c-va-label c-va-label-gap">
          {portalText("callerProtection", "analytics.refused_title")} · {countText(refusedTotal)}
        </p>
        <p className="c-note">{portalText("callerProtection", "analytics.refused_note")}</p>
        <PartyReasonTable caption={portalText("callerProtection", "analytics.refused_title")} counts={refusedByReason} />
      </div>
      <div>
        <p className="c-va-label c-va-label-gap">
          {portalText("callerProtection", "analytics.ended_title")} · {countText(endedTotal)}
        </p>
        <p className="c-note">{portalText("callerProtection", "analytics.ended_note")}</p>
        <PartyReasonTable caption={portalText("callerProtection", "analytics.ended_title")} counts={endedByReason} />
      </div>
    </div>
  );
}

/** A whole number in the reader's language. */
function countText(count: number): string {
  return count.toLocaleString(portalLocale() === "el" ? "el-GR" : "en-GB");
}

/**
 * plan-021 (design §33.13.4): the "Passes" block. Its figures are counts over the window's conversations this
 * VoiceAgent acted in; the share is the API's, shown as a whole percentage ("—" when it answered none first).
 */
export function VoiceAgentPassBlock({ block }: { readonly block: AnalyticsVoiceAgentPassView }): ReactNode {
  usePortalTranslation("voiceAgentPassHistory");
  const title = <p className="c-va-label c-va-label-gap">{portalText("voiceAgentPassHistory", "analytics.title")}</p>;
  if (analyticsVoiceAgentPassIsEmpty(block)) {
    return (
      <>
        {title}
        <p className="c-note">{portalText("voiceAgentPassHistory", "analytics.empty")}</p>
      </>
    );
  }
  const figure: readonly { readonly label: string; readonly value: string }[] = [
    { label: portalText("voiceAgentPassHistory", "analytics.out"), value: countText(block.passedOut.total) },
    { label: portalText("voiceAgentPassHistory", "analytics.in"), value: countText(block.passedIn.total) },
    { label: portalText("voiceAgentPassHistory", "analytics.refused"), value: countText(block.refused.total) },
    {
      label: portalText("voiceAgentPassHistory", "analytics.share"),
      value: block.passedShare === null ? "—" : `${String(Math.round(block.passedShare * 100))}%`,
    },
    { label: portalText("voiceAgentPassHistory", "analytics.time_served"), value: talkTimeText(block.timeServedSecond) },
  ];
  return (
    <>
      {title}
      <div className="c-va-figures c-va-analytics-figures">
        {figure.map((one) => (
          <div key={one.label}>
            <span className="c-va-figure-value">{one.value}</span>
            <span className="c-va-figure-label">{one.label}</span>
          </div>
        ))}
      </div>
      <div className="c-va-split c-va-analytics-lower">
        <div>
          <p className="c-va-label">{portalText("voiceAgentPassHistory", "analytics.by_agent")}</p>
          {block.passedOut.byTarget.length === 0 && block.passedIn.bySource.length === 0 ? (
            <p className="c-note">{portalText("operations", "nothing_to_break_down_yet_2e29d2fe")}</p>
          ) : (
            <div className="c-va-plain-table">
              <Table caption={portalText("voiceAgentPassHistory", "analytics.by_agent")}>
                <TableBody>
                  {block.passedOut.byTarget.map((row) => (
                    <TableRow key={`out:${row.voiceAgentId}:${row.mode}`}>
                      <TableCell>{row.displayName}</TableCell>
                      <TableCell>
                        <span className="c-va-muted">
                          {portalText("voiceAgentPassHistory", "analytics.out")} · {voiceAgentPassModeTextOf(row.mode)}
                        </span>
                      </TableCell>
                      <TableCell numeric>{countText(row.count)}</TableCell>
                    </TableRow>
                  ))}
                  {block.passedIn.bySource.map((row) => (
                    <TableRow key={`in:${row.voiceAgentId}`}>
                      <TableCell>{row.displayName}</TableCell>
                      <TableCell>
                        <span className="c-va-muted">{portalText("voiceAgentPassHistory", "analytics.in")}</span>
                      </TableCell>
                      <TableCell numeric>{countText(row.count)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
        <div>
          <p className="c-va-label">{portalText("voiceAgentPassHistory", "analytics.back")}</p>
          <div className="c-va-plain-table">
            <Table caption={portalText("voiceAgentPassHistory", "analytics.back")}>
              <TableBody>
                <TableRow>
                  <TableCell>{portalText("voiceAgentPassHistory", "analytics.back_made")}</TableCell>
                  <TableCell numeric>{countText(block.passedBack.made)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{portalText("voiceAgentPassHistory", "analytics.back_received")}</TableCell>
                  <TableCell numeric>{countText(block.passedBack.received)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
          <p className="c-va-label c-va-label-gap">{portalText("voiceAgentPassHistory", "analytics.by_reason")}</p>
          {block.refused.byReason.length === 0 ? (
            <p className="c-note">{portalText("operations", "nothing_to_break_down_yet_2e29d2fe")}</p>
          ) : (
            <div className="c-va-plain-table">
              <Table caption={portalText("voiceAgentPassHistory", "analytics.by_reason")}>
                <TableBody>
                  {refusedByReasonTextOf(block.refused.byReason).map((row) => (
                    <TableRow key={row.text}>
                      <TableCell>{row.text}</TableCell>
                      <TableCell numeric>{countText(row.count)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/** The design writes months as three letters: `Sep`, never `Sept`. */
const MONTH = () => ([portalText("operations", "jan_efed3690"), portalText("operations", "feb_dc8415cc"), portalText("operations", "mar_c4ba0822"), portalText("operations", "apr_befde54a"), portalText("operations", "may_c94f4798"), portalText("operations", "jun_6d90df3b"), portalText("operations", "jul_b7375584"), portalText("operations", "aug_75629af5"), portalText("operations", "sep_fdd289e3"), portalText("operations", "oct_51327aef"), portalText("operations", "nov_bb9bfefd"), portalText("operations", "dec_997f59bc")] as const);

/** `2026-09-06` → `6 Sep`, read as a calendar day rather than an instant. */
function dayText(isoDay: string): string {
  const at = new Date(`${isoDay}T12:00:00Z`);
  if (Number.isNaN(at.getTime())) return isoDay;
  return `${String(at.getUTCDate())} ${MONTH()[at.getUTCMonth()] ?? ""}`;
}

/**
 * The calls-per-day line.
 *
 * It is a `<figure>` with a real `<figcaption>` rather than an `aria-label` on an SVG,
 * because the caption carries the two facts the shape cannot: which zone the days are cut in
 * and what the peak was. An empty window renders the caption and no line, which is the
 * honest drawing of "nothing happened".
 */
function DailyChart({
  daily,
}: {
  readonly daily: VoiceAgentAnalyticsView["daily"];
}): ReactNode {
  usePortalTranslation("operations");
  if (daily.length === 0) {
    return <p className="c-note">{portalText("operations", "no_conversations_in_this_window_cb5192fc")}</p>;
  }

  const peak = Math.max(...daily.map((one) => one.conversationCount), 1);
  const step = daily.length === 1 ? 0 : CHART.width / (daily.length - 1);
  const point = daily
    .map((one, index) => {
      const x = daily.length === 1 ? CHART.width / 2 : index * step;
      const y =
        CHART.floor - (one.conversationCount / peak) * (CHART.floor - CHART.ceiling);
      return `${String(Math.round(x))},${String(Math.round(y))}`;
    })
    .join(" ");
  const last = point.split(" ").at(-1)?.split(",") ?? [];
  const firstDay = daily[0]?.day ?? "";
  const lastDay = daily.at(-1)?.day ?? "";
  const lastCount = daily.at(-1)?.conversationCount ?? 0;

  return (
    <figure className="c-va-chart-figure">
      <svg
        className="c-va-chart"
        viewBox={`0 0 ${String(CHART.width)} ${String(CHART.height)}`}
        role="img"
        aria-label={portalText("operations", "conversations_a_day_value_to_value_busiest_day_value_bdf8e28d", { v0: String(firstDay), v1: String(lastDay), v2: String(String(peak)) })}
      >
        <line
          className="c-va-chart-axis"
          x1="0"
          y1={String(CHART.floor)}
          x2={String(CHART.width)}
          y2={String(CHART.floor)}
        />
        <polyline className="c-va-chart-line" points={point} />
        {last.length === 2 ? (
          <circle className="c-va-chart-point" cx={last[0]} cy={last[1]} r="3.5" />
        ) : null}
        <text className="c-va-chart-label" x="0" y={String(CHART.height - 4)}>
          {dayText(firstDay)}
        </text>
        <text
          className="c-va-chart-label"
          x={String(CHART.width)}
          y={String(CHART.height - 4)}
          textAnchor="end"
        >
          {dayText(lastDay)}
        </text>
        {/* The last point's own value, the way the design labels the end of the line. */}
        <text
          className="c-va-chart-peak"
          x={String(CHART.width - 10)}
          y={String(CHART.ceiling - 2)}
          textAnchor="end"
        >
          {String(lastCount)} {lastCount === 1 ? portalText("operations", "call_bc8d8647") : portalText("operations", "calls_51b6cb25")}
        </text>
      </svg>
    </figure>
  );
}

function CountTable({
  caption,
  slice,
  total,
}: {
  readonly caption: string;
  readonly slice: readonly AnalyticsSliceView[];
  readonly total: number;
}): ReactNode {
  usePortalTranslation("operations");
  if (slice.length === 0) return <p className="c-note">{portalText("operations", "nothing_to_break_down_yet_2e29d2fe")}</p>;
  const shares = sharesOf(
    slice.map((row) => row.conversationCount),
    total,
  );
  return (
    <div className="c-va-plain-table">
      <Table caption={caption}>
        <TableBody>
          {slice.map((row, index) => {
            const share = shares[index] ?? null;
            return (
              <TableRow key={row.label}>
                <TableCell>{row.label}</TableCell>
                <TableCell numeric>{row.conversationCount.toLocaleString((portalLocale() === "el" ? "el-GR" : "en-GB"))}</TableCell>
                <TableCell numeric>
                  <span className="c-va-muted">{share === null ? "—" : `${String(share)}%`}</span>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function ShareList({
  slice,
  total,
}: {
  readonly slice: readonly AnalyticsSliceView[];
  readonly total: number;
}): ReactNode {
  usePortalTranslation("operations");
  if (slice.length === 0) return <p className="c-note">{portalText("operations", "nothing_to_break_down_yet_2e29d2fe")}</p>;
  const shares = sharesOf(
    slice.map((row) => row.conversationCount),
    total,
  );
  return (
    <div className="c-va-share-list">
      {slice.map((row, index) => {
        const share = shares[index] ?? null;
        return (
          <div key={row.label}>
            <div className="c-va-share-head">
              <span>{row.label}</span>
              <span className="c-va-tabular">
                {share === null ? "—" : `${String(share)}%`}
              </span>
            </div>
            <div
              className="c-va-share-track"
              role="meter"
              aria-label={portalText("operations", "value_value_of_value_1b2228bf", { v0: String(row.label), v1: String(String(row.conversationCount)), v2: String(String(total)) })}
              aria-valuenow={row.conversationCount}
              aria-valuemin={0}
              aria-valuemax={total}
            >
              {/* The fill is a VALUE, not a length from the scale, so it travels
                  as a custom property and the stylesheet still decides what "filled"
                  looks like. Same mechanism as every other meter in the console. */}
              <div className="c-va-share-fill" style={meterStyle(share ?? 0)} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function BusiestHours({
  hour,
}: {
  readonly hour: VoiceAgentAnalyticsView["hour"];
}): ReactNode {
  usePortalTranslation("operations");
  if (hour.length === 0) return <p className="c-note">{portalText("operations", "no_hour_has_any_conversations_yet_e2efe773")}</p>;
  const day = dayOfHours(hour);
  const peak = Math.max(...day.map((one) => one.conversationCount), 1);

  // All twenty-four hours, each bar labelled with its own hour (review #114: three bars with only
  // the first and the last labelled could not say when the shop was busy).
  return (
    <ol className="c-va-hour-grid" aria-label={portalText("operations", "conversations_by_hour_of_day_f8c413ea")}>
      {day.map((one) => (
        <li
          key={one.hour}
          className="c-va-hour-column"
          title={`${String(one.hour).padStart(2, "0")}:00 — ${String(one.conversationCount)}`}
        >
          <span className="c-va-hour-track">
            {one.conversationCount === 0 ? null : (
              <span
                className={
                  one.conversationCount === peak ? "c-va-hour-bar c-va-hour-bar-peak" : "c-va-hour-bar"
                }
                style={hourStyle(one.conversationCount, peak)}
              />
            )}
          </span>
          <span
            className={one.conversationCount === 0 ? "c-va-hour-label c-va-hour-label-quiet" : "c-va-hour-label"}
            aria-label={`${String(one.hour).padStart(2, "0")}:00, ${String(one.conversationCount)} ${
              one.conversationCount === 1 ? portalText("operations", "conversation_78b6f944") : portalText("operations", "conversations_c5fb54c2")
            }`}
          >
            {String(one.hour).padStart(2, "0")}
          </span>
        </li>
      ))}
    </ol>
  );
}

/** Every hour of the day, 0–23, with the API's counts where it has one and zero elsewhere. */
export function dayOfHours(
  hour: VoiceAgentAnalyticsView["hour"],
): readonly { readonly hour: number; readonly conversationCount: number }[] {
  const byHour = new Map(hour.map((one) => [one.hour, one.conversationCount]));
  return Array.from({ length: 24 }, (_, index) => ({
    hour: index,
    conversationCount: byHour.get(index) ?? 0,
  }));
}

/**
 * One hour bar's height, as a custom property.
 *
 * A stylesheet cannot carry fourteen measured values, and the height IS the datum — so it
 * travels the same way every other meter's fill does, and the stylesheet still decides what a
 * bar looks like. A quiet hour is a short bar, never a hidden one: the minimum is the width
 * token's worth of presence, which the stylesheet sets.
 */
function hourStyle(count: number, peak: number): CSSProperties {
  const share = peak <= 0 ? 0 : Math.max(2, Math.round((count / peak) * 100));
  return { "--c-hour-bar": `${String(share)}%` } as CSSProperties;
}
